"use client";

/**
 * Trial-countdown + dunning banners. Rendered at the top of the authenticated
 * app shell. All logic is derived client-side from `subscription.expires_at`
 * and `subscription.status` — no new Firestore fields.
 *
 * Implements the "Dunning banner" and "Trial countdown banners" acceptance
 * criteria in [[tasks/web-app-billing-management]].
 */

import { useEffect, useState } from "react";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { redirectToBillingPortal } from "@/lib/billing";
import { computeTrialBanner } from "@/lib/subscription";

const DUNNING_DISMISSED_KEY = "zoey_dunning_dismissed_session";
const TRIAL_WARNING_DISMISSED_KEY = "zoey_trial_warning_dismissed_session";

function readSessionFlag(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeSessionFlag(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

export function BillingBanners() {
  const { state, loading } = useHouseholdSubscription();
  const { householdId } = useHouseholdContext();
  const { openPaywall } = usePaywall();

  // `true` by default to suppress SSR/first-paint flash; the effect below
  // reads the real sessionStorage value on mount (client only).
  const [dunningDismissed, setDunningDismissed] = useState(true);
  const [trialWarningDismissed, setTrialWarningDismissed] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    // Syncing dismiss flags from sessionStorage on mount — required because
    // the storage read must happen client-side only (we default to `true`
    // during SSR/first paint to avoid flashing a banner the user has already
    // dismissed this session).
    setDunningDismissed(readSessionFlag(DUNNING_DISMISSED_KEY));
    setTrialWarningDismissed(readSessionFlag(TRIAL_WARNING_DISMISSED_KEY));
  }, []);

  if (loading) return null;

  const trialBanner = computeTrialBanner(state);

  async function handleUpdatePayment() {
    setPortalError(null);
    setPortalLoading(true);
    try {
      await redirectToBillingPortal({ householdId: householdId ?? undefined });
    } catch (err) {
      console.error("redirectToBillingPortal failed", err);
      setPortalError(
        "Couldn't open the billing portal. Please try again.",
      );
      setPortalLoading(false);
    }
  }

  // Past-due dunning banner takes priority over trial banners.
  if (state.isPastDue && !dunningDismissed) {
    return (
      <div
        role="alert"
        className="bg-red-600 text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
      >
        <div className="flex-1">
          <p className="text-sm font-semibold">
            Payment failed. Update your payment method to keep your subscription active.
          </p>
          {portalError && (
            <p className="text-xs mt-1 text-red-100">{portalError}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleUpdatePayment}
            disabled={portalLoading}
            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-red-700 hover:bg-red-50 transition-colors disabled:opacity-60"
          >
            {portalLoading ? "Opening…" : "Update payment method"}
          </button>
          <button
            onClick={() => {
              writeSessionFlag(DUNNING_DISMISSED_KEY);
              setDunningDismissed(true);
            }}
            aria-label="Dismiss"
            className="p-1 rounded-full hover:bg-white/10"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (trialBanner.kind === "expired") {
    // Red, persistent, non-dismissible — soft lockout state.
    return (
      <div
        role="alert"
        className="bg-red-600 text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
      >
        <div className="flex-1">
          <p className="text-sm font-semibold">
            Your trial has ended. Subscribe to unlock AI features.
          </p>
          <p className="text-xs text-red-100 mt-0.5">
            Core features like posts, chats, chores and calendar are still available.
          </p>
        </div>
        <button
          onClick={() =>
            openPaywall({
              title: "Your trial has ended",
              subtitle:
                "Pick a plan to unlock meal suggestions, weekend plans, and receipt scanning again.",
            })
          }
          className="shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold bg-white text-red-700 hover:bg-red-50 transition-colors"
        >
          Subscribe
        </button>
      </div>
    );
  }

  if (
    (trialBanner.kind === "warning" || trialBanner.kind === "urgent") &&
    !trialWarningDismissed
  ) {
    const isUrgent = trialBanner.kind === "urgent";
    const days = trialBanner.days;
    const headline = isUrgent
      ? `Only ${days} day${days === 1 ? "" : "s"} left in your trial`
      : `Your free trial ends in ${days} day${days === 1 ? "" : "s"}`;
    const sub = isUrgent
      ? "Pick a plan now to avoid losing AI features."
      : "Pick a plan to keep meal suggestions, weekend plans, and receipt scanning.";
    return (
      <div
        role="alert"
        className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${
          isUrgent
            ? "bg-amber-500 text-amber-950"
            : "bg-amber-400 text-amber-950"
        }`}
      >
        <div className="flex-1">
          <p className="text-sm font-semibold">{headline}</p>
          <p className="text-xs opacity-80 mt-0.5">{sub}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() =>
              openPaywall({
                title: headline,
                subtitle: sub,
              })
            }
            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-950 text-white hover:bg-black transition-colors"
          >
            See plans
          </button>
          <button
            onClick={() => {
              writeSessionFlag(TRIAL_WARNING_DISMISSED_KEY);
              setTrialWarningDismissed(true);
            }}
            aria-label="Dismiss"
            className="p-1 rounded-full hover:bg-black/10"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return null;
}
